import { inputClass } from '../utils/formStyles';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiAlertCircle, FiCornerUpLeft, FiInbox, FiLayers, FiPaperclip, FiPlus, FiUpload, FiUserX, FiUsers, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { taskService } from '../services/taskService';
import { idpService } from '../services/idpService';
import { workflowService } from '../services/workflowService';
import { Stage, Task, Workflow } from '../types';
import { StageFormValues } from '../types/stageForms';
import LoadingSpinner from '../components/LoadingSpinner';
import Badge from '../components/Badge';
import Button, { IconButton } from '../components/Button';
import DataTable, { Column } from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import PageHeader from '../components/PageHeader';
import Tabs from '../components/Tabs';
import Modal from '../components/Modal';
import Field from '../components/Field';
import Avatar from '../components/Avatar';
import SearchInput from '../components/SearchInput';
import StageProgress from '../components/flow/StageProgress';
import StageForm, { missingRequiredFields } from '../components/StageForm';
import StageValues from '../components/StageValues';
import StageAssigneePicker, { AUTO_ASSIGN } from '../components/StageAssigneePicker';
import { formatDateToIST, localDateInput, relativeDue } from '../utils/dateUtils';
import { PERMISSIONS, hasPermission, isOversightTeam } from '../utils/roleUtils';
import { getStageForm, isEnquiryIntake, stageFieldLabels } from '../utils/stageFormRegistry';
import { apiErrorMessage } from '../utils/apiError';
import { humanizeStatus, isCompletedStatus, priorityRank, priorityTone, statusTone } from '../utils/status';
import { idpAttachments, reviewFieldLabels, reviewFields, reviewHeadline } from '../utils/idpEnquiry';
import { isQueueOversight, WorkflowQueue } from '../utils/workflowQueues';

const REVIEW_TAB = 'review';
const REGISTER_TAB = 'register';
const NO_ENQUIRY_WORKFLOW = 'No enquiry workflow is set up.';

const enquiryWorkflowOf = (list: Workflow[]) =>
  list.find((workflow) => {
    const first = [...(workflow.stages ?? [])].sort((a, b) => a.stageOrder - b.stageOrder)[0];
    return isEnquiryIntake(first?.stageName);
  });

type QuickFilter = 'all' | 'overdue' | 'rework';

interface StagePlace {
  position: number;
  total: number;
  stageName: string;
}

const sameName = (left?: string, right?: string) =>
  (left ?? '').trim().toLowerCase() === (right ?? '').trim().toLowerCase();

const firstStageOf = (workflow: Workflow) =>
  [...(workflow.stages ?? [])].sort((a, b) => a.stageOrder - b.stageOrder)[0];

/**
 * Enquiries for one workflow. With no queue this is the Tabins quotation page.
 * A queue limits the page to that workflow's stages and its own register form.
 */
const MyEnquiries = ({ queue }: { queue?: WorkflowQueue }) => {
  const { user, currentMember, sessionLoading, permissions } = useAuth();
  const navigate = useNavigate();

  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [workflowsLoaded, setWorkflowsLoaded] = useState(false);
  const [rows, setRows] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [search, setSearch] = useState('');
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('all');

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newWorkflowId, setNewWorkflowId] = useState('');
  const [newName, setNewName] = useState('');
  const [newValues, setNewValues] = useState<StageFormValues>({});
  const [newAssignee, setNewAssignee] = useState(AUTO_ASSIGN);
  const [newAssigneeBlocked, setNewAssigneeBlocked] = useState(false);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [createErrors, setCreateErrors] = useState<{ workflow?: string; name?: string; missing?: string[] }>({});
  const enquiryNumberRequest = useRef(0);
  const suggestedEnquiryNumber = useRef('');
  const [reviewTask, setReviewTask] = useState<Task | null>(null);
  const [deciding, setDeciding] = useState(false);

  // Only the oversight teams follow every enquiry. Everyone else sees the work
  // assigned to them, whatever their role happens to permit elsewhere.
  const canSeeRegister = queue
    ? isQueueOversight(queue, currentMember?.teamName)
    : isOversightTeam(currentMember?.teamName);
  const canRegister = hasPermission(permissions, PERMISSIONS.tasksManage);
  const pageTitle = queue?.title ?? 'Enquiries';

  // The quotation page keeps only the enquiry workflow. A queue page keeps one
  // named workflow, so its stages never appear under Enquiries.
  const scopedWorkflows = useMemo<Workflow[]>(() => {
    if (queue) return workflows.filter((workflow) => sameName(workflow.workflowName, queue.workflowName));
    return workflows.filter((workflow) => isEnquiryIntake(firstStageOf(workflow)?.stageName));
  }, [workflows, queue]);

  const scopedIds = useMemo(() => new Set(scopedWorkflows.map((workflow) => workflow.workflowId)), [scopedWorkflows]);

  // One tab per stage this member's team owns. Derived from the workflows call,
  // which already carries every stage with its team, so no extra endpoint is needed.
  const myStages = useMemo<Stage[]>(() => {
    if (!currentMember?.teamId) return [];
    return scopedWorkflows
      .flatMap((w) => w.stages ?? [])
      .filter((s) => s.teamId === currentMember.teamId)
      .sort((a, b) => a.stageOrder - b.stageOrder);
  }, [scopedWorkflows, currentMember]);

  const firstStageIds = useMemo(() => {
    const ids = new Set<string>();
    scopedWorkflows.forEach((w) => {
      const first = firstStageOf(w);
      if (first) ids.add(first.stageId);
    });
    return ids;
  }, [scopedWorkflows]);

  // Where every stage sits in its workflow, so a row can show "4 of 11" without
  // another request.
  const placeOf = useMemo(() => {
    const byWorkflow = new Map<string, Stage[]>();
    scopedWorkflows.forEach((w) => byWorkflow.set(w.workflowId, [...(w.stages ?? [])].sort((a, b) => a.stageOrder - b.stageOrder)));
    return (task: Task): StagePlace | null => {
      const ordered = byWorkflow.get(task.workflowId);
      if (!ordered || ordered.length === 0) return null;
      const index = ordered.findIndex(
        (s) => s.stageId === task.stageId || (!task.stageId && task.stageName && s.stageName === task.stageName)
      );
      return { position: index + 1, total: ordered.length, stageName: ordered[index]?.stageName ?? task.stageName ?? '' };
    };
  }, [scopedWorkflows]);

  const isIntakeTab = Boolean(activeTab && firstStageIds.has(activeTab));
  const ownsFirstStage = myStages.some((stage) => firstStageIds.has(stage.stageId));

  useEffect(() => {
    workflowService
      .getAll()
      .then((data) => setWorkflows(data ?? []))
      .catch(() => setError('Could not load the workflows.'))
      .finally(() => setWorkflowsLoaded(true));
  }, []);

  useEffect(() => {
    if (!workflowsLoaded) return;
    if (queue) {
      if (!activeTab) {
        if (myStages[0]) setActiveTab(myStages[0].stageId);
        else if (canSeeRegister) setActiveTab(REGISTER_TAB);
      }
      return;
    }
    if (scopedWorkflows.length === 0) return;
    if (ownsFirstStage) {
      if (!activeTab) setActiveTab(REVIEW_TAB);
      return;
    }
    if (!activeTab || activeTab === REVIEW_TAB) {
      if (myStages[0]) setActiveTab(myStages[0].stageId);
      else if (canSeeRegister) setActiveTab(REGISTER_TAB);
    }
  }, [workflowsLoaded, scopedWorkflows.length, ownsFirstStage, activeTab, myStages, canSeeRegister, queue]);

  const loadTab = useCallback(async () => {
    if (!user || !currentMember || !activeTab) return;
    try {
      setLoading(true);
      setError(null);

      if (activeTab === REVIEW_TAB) {
        setRows(await idpService.listReview());
        return;
      }

      if (activeTab === REGISTER_TAB) {
        const paged = await taskService.getAllPaginated(undefined, 1, 200);
        setRows((paged.data ?? []).filter((task) => scopedIds.has(task.workflowId)));
        return;
      }

      if (firstStageIds.has(activeTab)) {
        // The intake tab is a record of what this person raised, at whatever stage it
        // has since reached, rather than a queue of work waiting on them.
        const all = (await taskService.getAllPaginated(undefined, 1, 200)).data ?? [];
        setRows(all.filter((t) => t.createdByMemberId === currentMember.memberId && scopedIds.has(t.workflowId)));
        return;
      }

      const atStage = await taskService.getByStage(activeTab);
      setRows(atStage.filter((t) => t.assignedToMemberId === currentMember.memberId));
    } catch (err) {
      setError('Could not load these enquiries.');
      console.error('Error loading enquiries:', err);
    } finally {
      setLoading(false);
    }
  }, [user, currentMember, activeTab, firstStageIds, scopedIds]);

  useEffect(() => {
    if (sessionLoading) return;
    if (!currentMember) {
      setLoading(false);
      return;
    }
    loadTab();
  }, [loadTab, currentMember, sessionLoading]);

  // A new tab starts unfiltered; carrying "Overdue" across would hide its rows.
  useEffect(() => {
    setQuickFilter('all');
    setSearch('');
  }, [activeTab]);

  // Counts for the queue tabs, so work waiting elsewhere is visible without
  // clicking through every tab.
  useEffect(() => {
    if (!currentMember || myStages.length === 0) return;
    let cancelled = false;
    Promise.all(
      myStages
        .filter((s) => !firstStageIds.has(s.stageId))
        .map(async (s) => {
          const tasks = await taskService.getByStage(s.stageId).catch(() => [] as Task[]);
          return [s.stageId, tasks.filter((t) => t.assignedToMemberId === currentMember.memberId).length] as const;
        })
    ).then((pairs) => {
      if (!cancelled) setCounts(Object.fromEntries(pairs));
    });
    return () => {
      cancelled = true;
    };
  }, [myStages, currentMember, firstStageIds, rows]);

  const selectedWorkflow = workflows.find((w) => w.workflowId === newWorkflowId);
  const orderedNewStages = [...(selectedWorkflow?.stages ?? [])].sort((a, b) => a.stageOrder - b.stageOrder);
  const intakeSchema = getStageForm(orderedNewStages[0]?.stageName, selectedWorkflow?.workflowName);
  const secondStage = orderedNewStages[1];

  const closeCreate = () => {
    setCreateOpen(false);
    setNewWorkflowId('');
    setNewName('');
    setNewValues({});
    setNewAssignee(AUTO_ASSIGN);
    setNewFiles([]);
    setCreateErrors({});
  };

  const keepIntakeDefaults = (current: StageFormValues, enquiryNumber?: string): StageFormValues => ({
    ...current,
    enquiryNumber:
      typeof current.enquiryNumber === 'string' && current.enquiryNumber.trim()
        ? current.enquiryNumber
        : enquiryNumber ?? '',
    receivedOn:
      typeof current.receivedOn === 'string' && current.receivedOn
        ? current.receivedOn
        : localDateInput(),
  });

  const openCreate = () => {
    const request = ++enquiryNumberRequest.current;
    const workflow = queue
      ? scopedWorkflows.find((item) => sameName(item.workflowName, queue.workflowName))
      : enquiryWorkflowOf(scopedWorkflows);
    suggestedEnquiryNumber.current = '';
    setCreateOpen(true);
    setNewFiles([]);
    setNewWorkflowId(workflow?.workflowId ?? '');
    setCreateErrors(workflow ? {} : { workflow: queue ? `${queue.workflowName} is not set up.` : NO_ENQUIRY_WORKFLOW });
    setNewValues({ receivedOn: localDateInput() });
    if (queue) return;
    taskService
      .nextEnquiryNumber()
      .then((number) => {
        if (enquiryNumberRequest.current !== request) return;
        suggestedEnquiryNumber.current = number;
        setNewValues((current) => keepIntakeDefaults(current, number));
      })
      .catch(() => {
        if (enquiryNumberRequest.current !== request) return;
        toast.error('Could not assign an enquiry number. Type one to continue.');
      });
  };

  const waitForFirstStage = async (taskId: string) => {
    for (let attempt = 0; attempt < 10; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, 800));
      try {
        const fresh = await taskService.getById(taskId);
        if (fresh.stageId) return fresh;
      } catch {
        // still in flight
      }
    }
    return null;
  };

  const handleCreate = async (assignAfter = false) => {
    const missing = selectedWorkflow ? missingRequiredFields(intakeSchema, newValues) : [];
    const errors = {
      workflow: selectedWorkflow ? undefined : NO_ENQUIRY_WORKFLOW,
      name: newName.trim() ? undefined : 'Give the enquiry a name.',
      missing: missing.length > 0 ? missing : undefined,
    };
    setCreateErrors(errors);
    if (errors.workflow || errors.name || errors.missing) {
      if (errors.missing) toast.error(`Fill in: ${missing.join(', ')}`);
      return;
    }

    setCreating(true);
    try {
      const taskData: StageFormValues = {};
      intakeSchema.fields.forEach((field) => {
        if (newValues[field.name] !== undefined) taskData[field.name] = newValues[field.name];
      });
      if (
        suggestedEnquiryNumber.current &&
        taskData.enquiryNumber === suggestedEnquiryNumber.current
      ) {
        taskData.enquiryNumber = '';
      }

      const created = await taskService.create({
        taskName: newName.trim(),
        taskType: selectedWorkflow!.workflowName,
        taskData,
        // Sending the creator is what puts the first stage with them rather than
        // with whichever teammate happens to be least loaded.
        createdByMemberId: currentMember?.memberId,
      });

      const failedFiles: string[] = [];
      for (const file of newFiles) {
        try {
          await taskService.uploadAttachment(created.taskId, file);
        } catch {
          failedFiles.push(file.name);
        }
      }

      if (assignAfter) {
        const landed = await waitForFirstStage(created.taskId);
        if (landed) {
          await taskService.completeStage(created.taskId, undefined, newAssignee || undefined);
          toast.success(queue ? 'Registered and moved to the next stage.' : 'Enquiry registered and moved to the next stage.');
        } else {
          toast.warning(queue ? 'Registered, but it has not reached the first stage yet.' : 'Enquiry registered, but it has not reached the first stage yet.');
        }
      } else {
        toast.success(queue ? 'Registered.' : 'Enquiry registered.');
      }

      if (failedFiles.length > 0) {
        toast.warning(
          `Could not save ${failedFiles.join(', ')} to DMS. ${queue ? 'It was registered' : 'The enquiry was registered'} — attach the files from it.`
        );
      }

      closeCreate();
      await new Promise((resolve) => setTimeout(resolve, 1200));
      await loadTab();
    } catch (err: any) {
      toast.error(apiErrorMessage(err, queue ? 'Could not register it.' : 'Could not register the enquiry.'));
    } finally {
      setCreating(false);
    }
  };

  const decideReview = async (accept: boolean) => {
    if (!reviewTask || !currentMember) return;
    setDeciding(true);
    try {
      if (accept) {
        await idpService.acceptReview(reviewTask.taskId, currentMember.memberId);
        const acceptedId = reviewTask.taskId;
        void idpService.importDocuments(acceptedId).catch(() => {});
        toast.success('Enquiry accepted.');
        setReviewTask(null);
        const intake = myStages.find((stage) => firstStageIds.has(stage.stageId));
        await new Promise((resolve) => setTimeout(resolve, 1500));
        if (intake) setActiveTab(intake.stageId);
        else await loadTab();
      } else {
        await idpService.rejectReview(reviewTask.taskId);
        toast.success('Enquiry rejected.');
        setReviewTask(null);
        await loadTab();
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, accept ? 'Could not accept the enquiry.' : 'Could not reject the enquiry.'));
    } finally {
      setDeciding(false);
    }
  };

  if (sessionLoading) return <LoadingSpinner label="Loading your queue" />;

  if (!currentMember || (!currentMember.teamId && !canSeeRegister)) {
    const unlinked = !currentMember;
    return (
      <div>
        <PageHeader title={pageTitle} />
        <div className="card">
          <EmptyState
            icon={unlinked ? <FiUserX /> : <FiUsers />}
            title={unlinked ? "Your login isn't linked to a member" : "You're not on a team yet"}
            body={
              unlinked
                ? 'Work is assigned to members, so nothing can be shown until an administrator links your login to a member record on the Members page.'
                : 'Work reaches you through your team. Ask an administrator to add you to one on the Teams page, and your stages will appear here.'
            }
          />
        </div>
      </div>
    );
  }

  if (queue && workflowsLoaded && scopedWorkflows.length === 0) {
    return (
      <div>
        <PageHeader title={pageTitle} />
        <div className="card">
          <EmptyState
            icon={<FiInbox />}
            title={`${queue.workflowName} is not set up`}
            body="Run this workflow's seed, then the stages your team owns will appear here."
          />
        </div>
      </div>
    );
  }

  if (queue && workflowsLoaded && myStages.length === 0 && !canSeeRegister) {
    return (
      <div>
        <PageHeader title={pageTitle} />
        <div className="card">
          <EmptyState
            icon={<FiInbox />}
            title="No stages for your team"
            body={`${pageTitle} shows the stages assigned to ${currentMember.teamName}.`}
          />
        </div>
      </div>
    );
  }

  const activeStage = myStages.find((s) => s.stageId === activeTab);
  const isReview = activeTab === REVIEW_TAB;
  const isRegister = activeTab === REGISTER_TAB;
  const showProgress = isRegister || isIntakeTab;

  const term = search.trim().toLowerCase();
  const overdueCount = rows.filter((t) => t.isOverdue).length;
  const reworkCount = rows.filter((t) => t.needsRework).length;
  const visibleRows = rows.filter((t) => {
    if (quickFilter === 'overdue' && !t.isOverdue) return false;
    if (quickFilter === 'rework' && !t.needsRework) return false;
    if (!term) return true;
    return [t.taskName, t.stageName, t.assignedToMemberName, t.priority].some((v) => v?.toLowerCase().includes(term));
  });

  const dueCell = (t: Task) => {
    if (!t.dueDate) return <span className="text-ink-subtle">—</span>;
    const due = relativeDue(t.dueDate);
    const done = isCompletedStatus(t.status);
    return (
      <div className="text-right leading-tight">
        {!done && due && (
          <p className={`font-sans text-meta font-medium ${due.tone === 'danger' ? 'text-danger' : due.tone === 'warning' ? 'text-warning' : 'text-ink-muted'}`}>
            {due.label}
          </p>
        )}
        <p className="text-[11px] text-ink-subtle">{formatDateToIST(t.dueDate)}</p>
      </div>
    );
  };

  const columns: Column<Task>[] = [
    {
      key: 'taskName',
      header: 'Enquiry',
      sortValue: (t) => t.taskName.toLowerCase(),
      render: (t) => (
        <div className="min-w-0 max-w-[320px]">
          <span className="font-semibold text-ink block truncate" title={t.taskName}>{t.taskName}</span>
          {t.needsRework ? (
            <span className="mt-0.5 flex items-start gap-1.5 text-meta text-warning">
              <FiCornerUpLeft className="mt-0.5 shrink-0" aria-hidden="true" />
              <span className="line-clamp-2">
                <strong className="font-semibold">Sent back.</strong>
                {t.reworkReason ? ` ${t.reworkReason}` : ' Needs rework.'}
              </span>
            </span>
          ) : (
            <span className="block text-meta text-ink-subtle truncate">
              {isReview
                ? reviewHeadline(t)
                : workflows.find((w) => w.workflowId === t.workflowId)?.workflowName ?? ''}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'progress',
      header: showProgress ? 'Progress' : 'Stage',
      hideOnMobile: true,
      sortValue: (t) => placeOf(t)?.position ?? 0,
      render: (t) => {
        const place = placeOf(t);
        if (!place) return <span className="text-ink-muted">{t.stageName ?? '—'}</span>;
        return (
          <StageProgress
            total={place.total}
            current={place.position}
            done={isCompletedStatus(t.status)}
            rework={t.needsRework}
            stageName={place.stageName || t.stageName}
          />
        );
      },
    },
    ...(!isIntakeTab
      ? [{
          key: 'assignee',
          header: 'With',
          hideOnMobile: true,
          sortValue: (t: Task) => t.assignedToMemberName ?? '',
          render: (t: Task) =>
            t.assignedToMemberName ? (
              <span className="inline-flex items-center gap-2 text-ink">
                <Avatar name={t.assignedToMemberName} size="sm" />
                <span className="truncate max-w-[160px]">{t.assignedToMemberName}</span>
              </span>
            ) : (
              <span className="text-ink-subtle">Unassigned</span>
            ),
        } as Column<Task>]
      : []),
    {
      key: 'priority',
      header: 'Priority',
      sortValue: (t) => priorityRank(t.priority),
      render: (t) => <Badge tone={priorityTone(t.priority)}>{t.priority}</Badge>,
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (t) => (t.isOverdue ? 'zz-overdue' : t.status),
      render: (t) => (
        <Badge dot tone={statusTone(t.status, t.isOverdue)} icon={t.isOverdue ? <FiAlertCircle /> : undefined}>
          {t.isOverdue ? 'Overdue' : humanizeStatus(t.status)}
        </Badge>
      ),
    },
    {
      key: 'dueDate',
      header: 'Due',
      align: 'right',
      hideOnMobile: true,
      sortValue: (t) => t.dueDate ?? '',
      render: dueCell,
    },
  ];

  const mobileCard = (t: Task) => {
    const place = placeOf(t);
    const due = relativeDue(t.dueDate);
    return (
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-3">
          <span className="font-semibold text-ink leading-5">{t.taskName}</span>
          <Badge dot tone={statusTone(t.status, t.isOverdue)}>{t.isOverdue ? 'Overdue' : humanizeStatus(t.status)}</Badge>
        </div>
        {place && (
          <StageProgress total={place.total} current={place.position} done={isCompletedStatus(t.status)} rework={t.needsRework} stageName={place.stageName} />
        )}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-meta text-ink-subtle">
          <Badge tone={priorityTone(t.priority)}>{t.priority}</Badge>
          {t.assignedToMemberName && !isIntakeTab && <span>With {t.assignedToMemberName}</span>}
          {due && !isCompletedStatus(t.status) && <span className={due.tone === 'danger' ? 'text-danger font-medium' : ''}>{due.label}</span>}
        </div>
        {t.needsRework && (
          <p className="text-meta text-warning flex items-start gap-1.5">
            <FiCornerUpLeft aria-hidden="true" className="mt-0.5 shrink-0" />
            {t.reworkReason || 'Sent back — needs rework.'}
          </p>
        )}
      </div>
    );
  };

  const filterChip = (id: QuickFilter, label: string, count: number, tone?: 'danger' | 'warning') => {
    const active = quickFilter === id;
    return (
      <button
        type="button"
        aria-pressed={active}
        onClick={() => setQuickFilter(active && id !== 'all' ? 'all' : id)}
        className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-meta font-medium cursor-pointer ring-1 ring-inset ${
          active
            ? 'bg-ink text-white ring-ink'
            : 'bg-surface text-ink-muted ring-line-strong hover:text-ink hover:ring-[#A9B0C4]'
        }`}
      >
        {tone && !active && (
          <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${tone === 'danger' ? 'bg-danger' : 'bg-warning-strong'}`} />
        )}
        {label}
        <span className={`tabular ${active ? 'text-white/70' : 'text-ink-subtle'}`}>{count}</span>
      </button>
    );
  };

  const tabs = [
    ...(!queue && ownsFirstStage ? [{ id: REVIEW_TAB, label: 'Review' }] : []),
    ...myStages.map((stage) => ({
      id: stage.stageId,
      label: !queue && firstStageIds.has(stage.stageId) ? 'Enquiry' : stage.stageName,
      count: firstStageIds.has(stage.stageId) ? undefined : counts[stage.stageId] ?? undefined,
    })),
    ...(canSeeRegister ? [{ id: REGISTER_TAB, label: 'All enquiries', icon: <FiLayers /> }] : []),
  ];

  const subtitle = isReview
    ? 'Enquiries waiting for review.'
    : isRegister
      ? queue
        ? `Every ${queue.itemName}, wherever it is in its workflow.`
        : 'Every enquiry in the organisation, wherever it is in its workflow.'
      : isIntakeTab
        ? 'Enquiries you registered, and how far each has got.'
        : activeStage
          ? `Work waiting on you at ${activeStage.stageName}.`
          : undefined;

  return (
    <div>
      <PageHeader
        title={pageTitle}
        subtitle={subtitle}
        meta={
          <span className="inline-flex items-center gap-2">
            <Avatar name={`${currentMember.firstName} ${currentMember.lastName}`} size="xs" />
            {currentMember.firstName} {currentMember.lastName}
            {currentMember.teamName ? ` · ${currentMember.teamName}` : ''}
          </span>
        }
        actions={
          canRegister && isIntakeTab ? (
            <Button variant="primary" icon={<FiPlus />} onClick={openCreate}>
              {queue ? 'Register' : 'New Enquiry'}
            </Button>
          ) : null
        }
      />

      {tabs.length > 0 && (
        <div className="mb-5">
          <Tabs label="Enquiry queues" tabs={tabs} activeId={activeTab} onChange={setActiveTab} />
        </div>
      )}

      {error && (
        <div role="alert" className="mb-5 flex items-center gap-2.5 px-4 py-3 bg-danger-subtle border border-danger-border rounded-card text-danger text-body">
          <FiAlertCircle aria-hidden="true" className="shrink-0" />
          {error}
        </div>
      )}

      <DataTable<Task>
        caption={isReview ? 'Review' : activeStage ? `Enquiries at ${activeStage.stageName}` : 'Enquiries'}
        columns={columns}
        rows={visibleRows}
        rowKey={(t) => t.taskId}
        loading={loading}
        onRowClick={(t) => (isReview ? setReviewTask(t) : navigate(`/tasks/${t.taskId}`))}
        rowTone={(t) => (t.isOverdue ? 'danger' : t.needsRework ? 'warning' : undefined)}
        mobileCard={mobileCard}
        toolbar={
          rows.length > 0 || term ? (
            <>
              <SearchInput
                label="Search enquiries"
                placeholder="Search by name, stage or person"
                value={search}
                onChange={setSearch}
                className="w-full sm:w-72"
              />
              <div className="flex flex-wrap items-center gap-1.5 sm:ml-auto">
                {filterChip('all', 'All', rows.length)}
                {filterChip('overdue', 'Overdue', overdueCount, 'danger')}
                {filterChip('rework', 'Sent back', reworkCount, 'warning')}
              </div>
            </>
          ) : undefined
        }
        empty={
          rows.length > 0 ? (
            <EmptyState
              compact
              icon={<FiInbox />}
              title="Nothing matches"
              body="No enquiry in this queue matches the search or filter."
              action={
                <Button
                  size="sm"
                  onClick={() => {
                    setSearch('');
                    setQuickFilter('all');
                  }}
                >
                  Clear filters
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={<FiInbox />}
              title={
                isReview
                  ? 'Nothing to review'
                  : isIntakeTab
                    ? 'No enquiries registered yet'
                    : isRegister
                      ? 'No enquiries yet'
                      : 'Nothing waiting for you here'
              }
              body={
                isReview
                  ? 'Enquiries appear here when they are ready for review.'
                  : isIntakeTab
                    ? 'Register one and it will appear here as it moves through the workflow.'
                    : isRegister
                      ? 'Enquiries appear here as soon as the administrator registers them.'
                      : `Enquiries appear once one reaches ${activeStage?.stageName ?? 'this stage'} and is assigned to you.`
              }
              action={
                canRegister && isIntakeTab ? (
                  <Button variant="primary" icon={<FiPlus />} onClick={openCreate}>
                    {queue ? 'Register' : 'New Enquiry'}
                  </Button>
                ) : null
              }
            />
          )
        }
      />

      <Modal
        isOpen={createOpen}
        title={queue ? `Register a new ${queue.itemName}` : 'Register a new enquiry'}
        description="It starts at the first stage of the workflow and moves on from there."
        icon={<FiPlus />}
        size="xl"
        onClose={creating ? () => {} : closeCreate}
        footer={
          <>
            <Button variant="secondary" disabled={creating} onClick={closeCreate}>
              Cancel
            </Button>
            <Button variant="secondary" disabled={creating} onClick={() => handleCreate(false)}>
              {creating ? 'Working…' : queue ? 'Save' : 'Save enquiry'}
            </Button>
            <Button
              variant="primary"
              loading={creating}
              disabled={newAssigneeBlocked || !secondStage}
              onClick={() => handleCreate(true)}
            >
              {creating ? 'Working…' : 'Save & assign'}
            </Button>
          </>
        }
      >
        <div className="space-y-6">
          {createErrors.workflow && <p className="text-meta text-danger">{createErrors.workflow}</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field htmlFor="new-name" label={queue ? 'Name' : 'Enquiry name'} required error={createErrors.name} help={queue ? 'A name the team will recognise.' : 'Usually the enquiry number and the project.'}>
              <input
                id="new-name"
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={queue ? 'APP-0142 · North warehouse' : 'ENQ-0142 · Al Noor Tower'}
                aria-invalid={Boolean(createErrors.name)}
                className={`${inputClass} ${createErrors.name ? 'border-danger' : ''}`}
              />
            </Field>
          </div>

          {selectedWorkflow && orderedNewStages.length > 0 && (
            <div className="rounded-card bg-surface-muted border border-line px-4 py-3">
              <p className="eyebrow mb-2">Route</p>
              <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-meta">
                {orderedNewStages.map((stage, index) => (
                  <li key={stage.stageId} className="inline-flex items-center gap-1.5">
                    <span className={index === 0 ? 'font-semibold text-primary' : 'text-ink-muted'}>{stage.stageName}</span>
                    {index < orderedNewStages.length - 1 && <span aria-hidden="true" className="text-ink-subtle">→</span>}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {selectedWorkflow && (
            <div>
              <p className="eyebrow mb-3">{intakeSchema.title}</p>
              <StageForm
                bare
                schema={intakeSchema}
                values={newValues}
                onChange={(values) => {
                  setNewValues((current) => {
                    const next: StageFormValues = {
                      ...current,
                      ...values,
                      receivedOn: values.receivedOn || current.receivedOn || localDateInput(),
                    };
                    if (!String(values.enquiryNumber ?? '').trim() && current.enquiryNumber)
                      next.enquiryNumber = current.enquiryNumber;
                    return next;
                  });
                  if (createErrors.missing)
                    setCreateErrors((e) => ({ ...e, missing: missingRequiredFields(intakeSchema, values) }));
                }}
                readOnly={creating}
                stages={orderedNewStages}
                missing={createErrors.missing}
              />
            </div>
          )}

          <div className="pt-5 border-t border-line-subtle">
            <div className="flex items-center justify-between gap-3 mb-3">
              <p className="eyebrow">Files</p>
              <label
                className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-control border border-line-strong bg-surface text-meta font-medium text-ink shadow-azure-sm ${
                  creating ? 'opacity-50 cursor-not-allowed' : 'hover:bg-surface-muted cursor-pointer'
                } focus-within:shadow-focus`}
              >
                <FiUpload aria-hidden="true" />
                Add file
                <input
                  type="file"
                  multiple
                  className="sr-only"
                  disabled={creating}
                  onChange={(event) => {
                    const picked = Array.from(event.target.files ?? []);
                    event.target.value = '';
                    const empty = picked.filter((file) => file.size <= 0);
                    if (empty.length > 0) toast.error('Empty files cannot be saved.');
                    const usable = picked.filter((file) => file.size > 0);
                    if (usable.length === 0) return;
                    setNewFiles((current) => {
                      const next = [...current];
                      usable.forEach((file) => {
                        const exists = next.some(
                          (kept) => kept.name === file.name && kept.size === file.size && kept.lastModified === file.lastModified
                        );
                        if (!exists) next.push(file);
                      });
                      return next;
                    });
                  }}
                />
              </label>
            </div>
            {newFiles.length === 0 ? (
              <p className="text-meta text-ink-subtle flex items-center gap-1.5">
                <FiPaperclip aria-hidden="true" />
                {queue ? 'Optional. Files are saved to DMS with this stage.' : 'Optional, files related to this Enquiry.'}
              </p>
            ) : (
              <ul className="divide-y divide-line-subtle rounded-card border border-line">
                {newFiles.map((file) => (
                  <li key={`${file.name}-${file.size}-${file.lastModified}`} className="flex items-center gap-2 px-3 py-2">
                    <FiPaperclip className="shrink-0 text-ink-muted" aria-hidden="true" />
                    <span className="text-body text-ink min-w-0 flex-1 truncate" title={file.name}>
                      {file.name}
                    </span>
                    <IconButton
                      size="sm"
                      label={`Remove ${file.name}`}
                      icon={<FiX />}
                      disabled={creating}
                      onClick={() =>
                        setNewFiles((current) =>
                          current.filter(
                            (kept) =>
                              !(kept.name === file.name && kept.size === file.size && kept.lastModified === file.lastModified)
                          )
                        )
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>

          {selectedWorkflow && secondStage && (
            <div className="pt-5 border-t border-line-subtle max-w-md">
              <StageAssigneePicker
                teamId={secondStage.teamId}
                teamName={secondStage.teamName}
                stageName={secondStage.stageName}
                value={newAssignee}
                onChange={setNewAssignee}
                onBlockedChange={setNewAssigneeBlocked}
                disabled={creating}
              />
              <p className="mt-1.5 text-meta text-ink-subtle">Used by “Save &amp; assign”, which registers it and hands it straight on.</p>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={reviewTask != null}
        title={reviewTask?.taskName ?? 'Review'}
        description={reviewTask ? reviewHeadline(reviewTask) : undefined}
        size="lg"
        onClose={deciding ? () => {} : () => setReviewTask(null)}
        footer={
          <>
            <Button variant="danger" disabled={deciding} onClick={() => { void decideReview(false); }}>
              Reject
            </Button>
            <Button variant="primary" loading={deciding} onClick={() => { void decideReview(true); }}>
              Accept
            </Button>
          </>
        }
      >
        {reviewTask && (
          <div className="space-y-5">
            <StageValues
              values={reviewFields(reviewTask)}
              labels={{ ...stageFieldLabels('Receive & register enquiry'), ...reviewFieldLabels }}
            />
            {idpAttachments(reviewTask).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {idpAttachments(reviewTask).map((file) => (
                  <Button
                    key={file.id}
                    size="sm"
                    onClick={() => {
                      void idpService.downloadAttachment(file.id, file.filename).catch(() => toast.error('Could not download that file.'));
                    }}
                  >
                    {file.filename}
                  </Button>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MyEnquiries;
